import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { NewsService } from '../../../shared/services/news/news.service';
import { DeleteComponent } from '../../../shared/components/delete/delete.component';
import { CustomPipe } from '../../../shared/pipes/custom.pipe';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { MailTitlePipe } from '../../../shared/pipes/mail-title.pipe';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { TagsPipe } from '../../../shared/pipes/tags.pipe';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { environment } from 'src/environments/environment';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-activities',
  imports: [
    MatIcon,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    CustomPipe,
    TitleComponent,
    MailTitlePipe,
    RouterOutlet,
    TagsPipe,
    LoaderComponent,
    QeqPipe,
    MatTooltipModule
  ],
  templateUrl: './activities.component.html',
  styleUrls: [
    './activities.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ActivitiesComponent implements OnDestroy {
  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];
  private navigationSubscription: Subscription | null = null;

  title: string = 'Actividades';
  subtitle: string = 'Gestiona las actividades de la empresa.';

  programmed = new FormControl<string[]>([]);
  programmedList: string[] = ['Programada', 'No programada'];
  programmedMap: { [key: string]: boolean } = {
    Programada: true,
    'No programada': false,
  };

  featured = new FormControl<string[]>([]);
  featuredList: string[] = ['Destacada', 'No destacada'];
  featuredMap: { [key: string]: boolean } = {
    Destacada: true,
    'No destacada': false,
  };

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private newsService: NewsService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private rolesService: RolesService,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private snackBar: MatSnackBar,
  ) {}

  selectedFilter: string = 'todo';

  filters = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Últimos 7 días' },
    { value: 'hoy', label: 'Hoy' },
    { value: 'todo', label: 'Todo' },
  ];

  selectFilter(filter: string) {
    this.selectedFilter = filter;
    this.applyAllFilters();
  }

  // Helper methods for date filtering
  private getStartDateForFilter(filter: string): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today

    switch (filter) {
      case 'mes':
        const monthStart = new Date(today);
        monthStart.setDate(1); // First day of current month
        return monthStart;

      case 'semana':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - 6); // 7 days ago (including today)
        return weekStart;

      case 'hoy':
        return today;

      case 'todo':
      default:
        return null; // No date filtering
    }
  }

  // Method to apply all filters together (programmed, featured, and date)
  applyAllFilters() {
    const programmedValues = this.getProgrammedFilterValues();
    const featuredValues = this.getFeaturedFilterValues();
    const startDate = this.getStartDateForFilter(this.selectedFilter);

    const filterValue = JSON.stringify({
      programmedFilter: programmedValues,
      featuredFilter: featuredValues,
      dateFilter: startDate ? startDate.getTime() : null, // Convert to timestamp for comparison
    });

    this.dataSource.filter = filterValue;
  }

  readonly dialog = inject(MatDialog);

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  private _liveAnnouncer = inject(LiveAnnouncer);
  imageBaseUrl = 'https://midominio.com/media/';

  displayedColumns: string[] = [
    'title',
    'author',
    'published-date',
    'notifications',
    'featured',
    'tags',
    'images_miniaturas',
    'acciones',
    
  ];

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.setupCustomSorting();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  deleteSelected(id: number, nick: string) {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '550px',
      data: {
        title: '¿Seguro que quieres eliminar esta actividad?',
        message: nick,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        handler: () => {
          return this.newsService.deleteActivity(id);
        },
      },
    });

    // Esperar a que el diálogo se cierre y actualizar la tabla
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.dataSource.data = this.dataSource.data.filter(
          (user) => user.id !== id
        );
      }
    });
  }

  myroles: any = {}; // Change from array to object

  ngOnInit() {
    this.loaderService.showLoader();

    // Setup navigation listener
    this.setupNavigationListener();

    // Load roles and activities data
    this.loadRoles();
    this.loadActivitiesData();

    // Setup filter subscriptions
    this.setupFilterSubscriptions();
    this.imageBaseUrl = environment.imatgesComentarisAzureBlobURL;
  }

  setupNavigationListener() {
    // Clean up existing navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }

    // Set up new navigation subscription
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (
          this.router.url.includes('/communication/activities/edit') ||
          this.router.url.includes('/communication/activities/add')
        ) {
          this.isUuid = true;
        } else if (this.router.url.includes('/communication/activities')) {
          this.isUuid = false;

          // Clear existing subscriptions before reloading data
          this.clearSubscriptions();

          // Reload data
          this.loadRoles();
          this.loadActivitiesData();
          this.setupFilterSubscriptions();
        }

        this.cdr.detectChanges();
      });
  }

  loadRoles() {
    // Use the refactored method in RolesService
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'activities',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    } else {
      // console.log(
      //   'No user roles found in storage or no permissions for Comunicación'
      // );
    }
  }

  loadActivitiesData() {
    const activitiesSub = this.newsService.getActivities().subscribe({
      next: (activities) => {
        // console.log('Activities fetched:', activities);
        let filteredActivities = activities.sort((a, b) => {
          const dateA = new Date(
            a.publication_date ?? a.created_when ?? 0
          ).getTime();
          const dateB = new Date(
            b.publication_date ?? b.created_when ?? 0
          ).getTime();
          return dateB - dateA; // Descending order (most recent first)
        });
        this.dataSource = new MatTableDataSource(filteredActivities);
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;

        // Set up custom sorting
        this.setupCustomSorting();

        // Set up the filter predicate for programmed, featured, and date filtering
        this.setupFilterPredicate();

        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching activities', error);
        this.loaderService.hideLoader();
      },
    });

    this.subscriptions.push(activitiesSub);
  }
  moderarImagen(imagen:any){
    this.newsService.setModerationImageEvent(imagen.id, !imagen.is_moderated)
      .subscribe({
        next: () => {
          console.log('Actualizado correctamente');
          this.snackBar.open('Imagen moderada.','Cerrar', {duration: 3000,});
          imagen.is_moderated = !imagen.is_moderated;
        },
        error: (err) => {
          console.error('Error actualizando', err);
          this.snackBar.open('ERROR! No se ha actualizado.','Cerrar', {duration: 10000,});
        }
      }); 
  }

  setupFilterPredicate() {
    // Set up the filter predicate for programmed, featured, and date filtering
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter);
      let programmedMatch = true;
      let featuredMatch = true;
      let dateMatch = true;

      // Check programmed filter - note: published logic is inverted
      if (filterObj.programmedFilter && filterObj.programmedFilter.length > 0) {
        // Convert published (true=immediate, false=scheduled) to scheduled_publication logic
        const isScheduled = !data.published;
        programmedMatch = filterObj.programmedFilter.includes(isScheduled);
      }

      // Check featured filter
      if (filterObj.featuredFilter && filterObj.featuredFilter.length > 0) {
        featuredMatch = filterObj.featuredFilter.includes(data.featured);
      }

      // Check date filter
      if (filterObj.dateFilter) {
        // Use publication_date for scheduled (published=false), created_when for immediate (published=true)
        const dateToCheck = !data.published
          ? new Date(data.publication_date ?? data.created_when ?? 0).getTime()
          : new Date(data.created_when ?? data.publication_date ?? 0).getTime();
        dateMatch = dateToCheck >= filterObj.dateFilter;
      }

      // Item passes filter only if it matches all conditions
      return programmedMatch && featuredMatch && dateMatch;
    };
  }

  setupFilterSubscriptions() {
    // Subscribe to changes in the programmed control
    const programmedSub = this.programmed.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });

    this.subscriptions.push(programmedSub);

    // Subscribe to changes in the featured control
    const featuredSub = this.featured.valueChanges.subscribe(() => {
      this.applyAllFilters();
    });

    this.subscriptions.push(featuredSub);
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  ngOnDestroy() {
    // Clean up all subscriptions when component is destroyed
    this.clearSubscriptions();

    // Also clean up navigation subscription if it exists
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
  }

  isUuid: boolean = false;
  goToMail(element: any) {
    this.router.navigate(['home/communication/activities/edit', element.id]);
    this.isUuid = true;
  }

  goToNewMail() {
    this.router.navigate(['home/communication/activities/add']);
    this.isUuid = true;
  }

  // Set up custom sort functions for the table columns
  private setupCustomSorting() {
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'published-date':
          // Sort by publication_date if scheduled (published=false), otherwise by created_when
          return !item.published
            ? new Date(
                item.publication_date ?? item.created_when ?? 0
              ).getTime()
            : new Date(
                item.created_when ?? item.publication_date ?? 0
              ).getTime();
        case 'notifications':
          // Sort by boolean value (true first)
          return item.notifications ? 0 : 1;
        case 'featured':
          // Sort by boolean value (true first)
          return item.featured ? 0 : 1;
        case 'tags':
          // Sort by number of tags
          return item.tags ? item.tags.length : 0;
        default:
          return item[property];
      }
    };
  }

  // Helper method to get current programmed filter values
  private getProgrammedFilterValues(): boolean[] {
    const selectedProgrammed = this.programmed.value;
    if (!selectedProgrammed || selectedProgrammed.length === 0) {
      return [];
    }
    return selectedProgrammed.map((progName) => this.programmedMap[progName]);
  }

  // Helper method to get current featured filter values
  private getFeaturedFilterValues(): boolean[] {
    const selectedFeatured = this.featured.value;
    if (!selectedFeatured || selectedFeatured.length === 0) {
      return [];
    }
    return selectedFeatured.map((featName) => this.featuredMap[featName]);
  }
}
